import L from "leaflet";
import { resolverGoogleMapsLinkAsync } from "../../../../../comun/services/enlaces/EnlacesResolverGoogleMapsService";
import {
  departamentoGrpcCatalogService,
  StoredDepartamento,
} from "../../../../../modulos/catalogos/terceros/services/DepartamentoGrpcCatalogService";
import {
  municipioByDepartamentoIdGrpcService,
  StoredMunicipio,
} from "../../../../../modulos/catalogos/terceros/services/MunicipioByDepartamentoIdGrpcService";
import template from "./aoiSearchBarTemplate.html";

/** Patrones de coordenadas dentro de un link de Google Maps (mismos que el
 *  prototipo). Links largos (los que ya traen `lat,lng` visibles en el
 *  texto) se parsean directo; links acortados (`maps.app.goo.gl`/
 *  `goo.gl/maps`, sin coordenada en el texto) se resuelven primero del lado
 *  servidor (`resolverGoogleMapsLinkAsync`, sigue la redirección — el
 *  navegador no puede por CORS) y el resultado se vuelve a pasar por estos
 *  mismos patrones. */
const GOOGLE_MAPS_URL_PATTERNS = [
  /[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
  // `/maps/search/4.445315,+-75.740517` — es a esto a lo que Google redirige hoy
  // los links acortados de "Compartir". El separador lleva un `+` (espacio
  // codificado, la URL nunca se decodifica antes de pasar por acá), así que la
  // coma sola no alcanza.
  /\/maps\/(?:search|place)\/(-?\d+(?:\.\d+)?),[+\s]*(-?\d+(?:\.\d+)?)/,
  /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
  /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
];

/** Links acortados de Google Maps (botón "Compartir" del celular) — no
 *  traen coordenada en el texto, hace falta resolverlos del lado servidor. */
const GOOGLE_MAPS_SHORT_URL_PATTERN = /(maps\.app\.goo\.gl|goo\.gl\/maps)\//i;

/** Zoom al aterrizar por link de Google Maps: deja el punto con contexto
 *  alrededor (vías, predios vecinos) sin perder el detalle del predio. */
const ZOOM_ENLACE = 16;

/** Tope, en ms, para dar por terminado el viaje del mapa al punto del enlace
 *  si `moveend` no llega (el mapa ya estaba ahí). Holgado frente a la
 *  animación de `setView`, que dura ~250 ms. */
const TOPE_VIAJE_MS = 1500;

/** Ícono del marcador de búsqueda — Phosphor en vez del ícono default de
 *  Leaflet (`marker-icon.png`): bundleado con esbuild, Leaflet no puede
 *  autodetectar esa ruta (la busca relativa a un `<script src="leaflet.js">`
 *  que acá no existe) y el PNG da 404. Único `L.marker` del proyecto. */
const SEARCH_MARKER_ICON = L.divIcon({
  className: "aoi-search-marker",
  // Peso "fill" de Phosphor: clase base `ph-fill` (font-family propia,
  // Phosphor-Fill) + `ph-map-pin` — NO `ph-map-pin-fill` (no existe, el CSS
  // define `.ph-fill.ph-map-pin:before`).
  html: '<i class="ph-fill ph-map-pin"></i>',
  iconSize: [44, 44],
  iconAnchor: [22, 44],
});

function parseCoordsFromGoogleMapsUrl(
  url: string,
): { lat: number; lng: number } | null {
  for (const pattern of GOOGLE_MAPS_URL_PATTERNS) {
    const match = url.match(pattern);
    if (match) return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
  }
  return null;
}

/** Barra de búsqueda de ubicación del mapa del paso 3 del wizard: una sola
 *  fila de 3 columnas (Enlace Google Maps · Departamento · Municipio), sin
 *  pestañas. Clase simple (no `IConnectRender`, igual que `RigthActions`/
 *  `TopActions`): necesita la instancia del mapa en `connect`. Selects
 *  independientes (sin `InputSelectField`: no hay ningún `*Request` detrás,
 *  esto solo navega el mapa).
 *
 *  **Los dos caminos son excluyentes, y gana el último que el usuario tocó**:
 *  buscar por coordenada y filtrar por división administrativa son dos formas
 *  distintas de llegar al mismo punto. Elegir departamento vacía el enlace
 *  (`_limpiarEnlace`) en vez de deshabilitar los selects — deshabilitarlos
 *  dejaba al usuario encerrado en el enlace, teniendo que borrarlo a mano para
 *  poder usar el otro camino. Municipio sí se deshabilita, pero por su propia
 *  dependencia: no tiene sentido sin departamento elegido.
 *
 *  El input del enlace se colorea con el resultado de la última búsqueda
 *  (`is-ok`/`is-error`), y el color se descarta apenas se edita el texto: ya no
 *  corresponde a lo que está escrito.
 *
 *  El select de departamento no toca el mapa directamente — notifica a
 *  `AoiMapRender` (dueño de los layers) vía `onDepartamentoChange`, que
 *  centra/hace zoom, quita el tooltip y dibuja los municipios; acá solo se
 *  cachea el bounds del departamento para poder recentrar ahí al vaciar el
 *  select de municipio. El de municipio solo centra/hace zoom directo
 *  (`fitBounds`) — igual que el departamento, **nunca** toca
 *  `maxBounds`/`minZoom`: esos quedan fijos a nivel país todo el tiempo
 *  (seteados una sola vez en `AoiMapRender._drawDepartamentos`), el usuario
 *  siempre puede alejarse a ver todo el país sin que el paneo quede
 *  restringido a la selección actual. Todos los movimientos del mapa van
 *  animados (`animate: true`). */
export class AoiSearchBarRender {
  private _map: L.Map | null = null;
  private _searchMarker: L.Marker | null = null;
  private _departamentos: StoredDepartamento[] = [];
  /** Municipios del departamento actualmente elegido en el select (no los
   *  ~1122 del país a la vez: se piden por `departamentoId` bajo demanda, ver
   *  `MunicipioByDepartamentoIdGrpcService`). */
  private _municipios: StoredMunicipio[] = [];
  /** Guarda de carrera: si el usuario cambia de departamento antes de que la
   *  petición anterior resuelva, esa respuesta vieja se descarta. */
  private _selectedDepartamentoId: string | null = null;
  private _departamentoBounds: L.LatLngBounds | null = null;
  private _onDepartamentoChange: ((departamentoId: string | null) => void) | null =
    null;

  /** Número de la búsqueda por enlace vigente. Se incrementa al lanzar una
   *  nueva y al limpiar el enlace; las respuestas de una búsqueda que ya no
   *  es la vigente se descartan (ver `_bindLinkSearch`). */
  private _busquedaId = 0;

  private _linkInput: HTMLInputElement | null = null;
  private _linkClearBtn: HTMLButtonElement | null = null;
  private _linkError: HTMLElement | null = null;
  private _selectDepartamento: HTMLSelectElement | null = null;
  private _selectMunicipio: HTMLSelectElement | null = null;

  render(): string {
    return template;
  }

  connect(
    map: L.Map,
    onDepartamentoChange: (departamentoId: string | null) => void,
  ): void {
    this._map = map;
    this._onDepartamentoChange = onDepartamentoChange;

    this._linkInput = document.getElementById("aoiSearchLinkInput") as HTMLInputElement | null;
    this._linkClearBtn = document.getElementById("aoiSearchLinkClear") as HTMLButtonElement | null;
    this._linkError = document.getElementById("aoiSearchLinkError");
    this._selectDepartamento = document.getElementById("aoiSearchDepartamento") as HTMLSelectElement | null;
    this._selectMunicipio = document.getElementById("aoiSearchMunicipio") as HTMLSelectElement | null;

    this._bindLinkSearch();
    this._bindUbicacionSelects();
  }

  dispose(): void {
    this._map = null;
  }

  /** Resultado de la última búsqueda por enlace, en el borde del input: verde si
   *  se extrajo la coordenada, rojo si no, sin color mientras no haya resultado. */
  private _setLinkEstado(estado: "ok" | "error" | null): void {
    if (!this._linkInput) return;
    this._linkInput.classList.toggle("is-ok", estado === "ok");
    this._linkInput.classList.toggle("is-error", estado === "error");
  }

  /** Vacía el enlace y todo lo que cuelga de él: valor, color de resultado,
   *  mensaje de error, el botón de borrar y el MARCADOR que dejó en el mapa.
   *  Lo usa el botón (x) y también el select de departamento, que al elegir
   *  descarta el enlace.
   *
   *  Invalida además la búsqueda en curso (`_busquedaId`): si el enlace
   *  acortado todavía se estaba resolviendo en el servidor, su respuesta
   *  llegaría DESPUÉS de borrar y volvería a poner el marcador y a mover el
   *  mapa, como si el borrado no hubiera pasado. */
  private _limpiarEnlace(): void {
    this._busquedaId++;
    this._setCargando(false);
    if (this._linkInput) {
      this._linkInput.value = "";
      this._linkInput.disabled = false;
    }
    this._setLinkEstado(null);
    if (this._linkError) this._linkError.hidden = true;
    if (this._linkClearBtn) this._linkClearBtn.hidden = true;
    this._quitarMarcador();
  }

  private _quitarMarcador(): void {
    if (this._searchMarker) this._map?.removeLayer(this._searchMarker);
    this._searchMarker = null;
  }

  /** Alterna el botón del enlace entre la (x) de borrar y el indicador de
   *  carga. Cargando: visible, sin click y con el ícono girando (ver
   *  `.aoi-search-clear.is-loading` en `aoiMap.css`). */
  private _setCargando(cargando: boolean): void {
    const boton = this._linkClearBtn;
    if (!boton) return;
    const icono = boton.querySelector("i");
    boton.classList.toggle("is-loading", cargando);
    boton.disabled = cargando;
    if (icono) {
      icono.className = cargando ? "ph ph-circle-notch" : "ph ph-x";
    }
    const etiqueta = cargando ? "Buscando ubicación…" : "Borrar enlace";
    boton.title = etiqueta;
    boton.setAttribute("aria-label", etiqueta);
    if (cargando) boton.hidden = false;
  }

  private _bindLinkSearch(): void {
    const input = this._linkInput;
    const error = this._linkError;
    const clearBtn = this._linkClearBtn;
    if (!input || !error) return;

    const showError = (message: string) => {
      error.hidden = false;
      error.textContent = message;
      this._setLinkEstado("error");
    };

    // Toda búsqueda lleva su número (`_busquedaId`). Cada paso asíncrono
    // comprueba que siga siendo la vigente: si en el medio el usuario borró
    // el enlace, eligió un departamento o lanzó otra búsqueda, el resultado
    // viejo se descarta sin tocar el mapa ni el input.
    const esVigente = (id: number) => id === this._busquedaId;

    const goToCoords = (
      id: number,
      coords: { lat: number; lng: number } | null,
    ) => {
      if (!esVigente(id)) return;
      if (!coords) {
        this._setCargando(false);
        showError(
          "No se pudieron extraer coordenadas del enlace. Use un enlace que contenga lat,lng (ej: ?q=4.4927,-75.7292).",
        );
        return;
      }
      this._setLinkEstado("ok");
      // La carga sigue hasta que el mapa TERMINA de llegar al punto, no solo
      // hasta tener la coordenada: el viaje animado es parte de "buscar".
      this._goToPoint(coords.lat, coords.lng, ZOOM_ENLACE).then(() => {
        if (esVigente(id)) this._setCargando(false);
      });
    };

    const doSearch = () => {
      error.hidden = true;

      const raw = input.value.trim();
      if (!raw) {
        this._setLinkEstado(null);
        return;
      }

      const id = ++this._busquedaId;
      this._setCargando(true);

      // Link largo (ya trae la coordenada en el texto): parseo directo, sin
      // pegarle al servidor.
      if (!GOOGLE_MAPS_SHORT_URL_PATTERN.test(raw)) {
        goToCoords(id, parseCoordsFromGoogleMapsUrl(raw));
        return;
      }

      // Link acortado: resolver del lado servidor (sigue la redirección),
      // recién ahí parsear la URL final. El input se bloquea mientras tanto
      // (antes lo hacía el botón "Buscar", que ya no existe).
      input.disabled = true;
      resolverGoogleMapsLinkAsync(raw)
        .then((urlResuelta) =>
          goToCoords(id, parseCoordsFromGoogleMapsUrl(urlResuelta)),
        )
        .catch((err) => {
          if (!esVigente(id)) return;
          this._setCargando(false);
          // err.message (no el objeto err completo): HttpError.name es
          // siempre "HttpError", pasar el objeto crudo antepone
          // "HttpError: " al mensaje real del server (ej. el 400 de link
          // inválido/vencido, que ya viene armado y legible).
          showError(err.message ?? err);
        })
        .finally(() => {
          // Si la búsqueda ya se descartó (ej. el usuario eligió un
          // departamento), `_limpiarEnlace` ya rehabilitó el input; devolverle
          // el foco acá se lo robaría al select que el usuario está usando.
          if (!esVigente(id)) return;
          input.disabled = false;
          input.focus();
        });
    };

    // El botón (x) solo aparece cuando hay algo que borrar. Editar el enlace
    // descarta el color del resultado anterior: ya no corresponde a lo escrito.
    // Dejarlo VACÍO a mano equivale a pulsar la (x): se va también el
    // marcador del mapa y se descarta la búsqueda que estuviera en curso.
    const syncEstadoInput = () => {
      const conEnlace = input.value.trim().length > 0;
      if (!conEnlace) {
        this._limpiarEnlace();
        return;
      }
      if (clearBtn) clearBtn.hidden = false;
    };

    input.addEventListener("input", () => {
      this._setLinkEstado(null);
      syncEstadoInput();
    });
    // `paste` dispara antes de que el valor esté en el input — de ahí el
    // salto de tick, para leer ya el texto pegado.
    input.addEventListener("paste", () => {
      setTimeout(() => {
        syncEstadoInput();
        doSearch();
      }, 0);
    });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") doSearch();
    });
    input.addEventListener("change", doSearch);

    clearBtn?.addEventListener("click", () => {
      this._limpiarEnlace();
      input.focus();
    });
  }

  private _bindUbicacionSelects(): void {
    const selectDepartamento = this._selectDepartamento;
    const selectMunicipio = this._selectMunicipio;
    if (!selectDepartamento || !selectMunicipio) return;

    departamentoGrpcCatalogService.getListAsync().then((departamentos) => {
      this._departamentos = departamentos;
      this._appendOptions(selectDepartamento, departamentos);
    });

    selectDepartamento.addEventListener("change", () => {
      // Elegir departamento descarta el enlace Y su marcador: son dos formas
      // distintas de llegar al mismo punto y la que manda es la última que el
      // usuario tocó. Antes el marcador se dejaba como referencia, pero un
      // punto de una búsqueda ya descartada confundía más de lo que ayudaba.
      this._limpiarEnlace();

      selectMunicipio.innerHTML = '<option value="">Seleccionar...</option>';
      this._municipios = [];
      const departamentoId = selectDepartamento.value || null;
      this._selectedDepartamentoId = departamentoId;
      this._onDepartamentoChange?.(departamentoId);

      if (!departamentoId) {
        selectMunicipio.disabled = true;
        this._departamentoBounds = null;
        return;
      }

      const departamento = this._departamentos.find(
        (d) => d.id === departamentoId,
      );
      if (departamento) {
        this._departamentoBounds = L.geoJSON(
          departamento.areaGeografica,
        ).getBounds();
      }

      selectMunicipio.disabled = true;
      municipioByDepartamentoIdGrpcService
        .getListAsync(departamentoId)
        .then((municipios) => {
          // El usuario ya cambió de departamento: esta respuesta quedó vieja.
          if (this._selectedDepartamentoId !== departamentoId) return;
          this._municipios = municipios;
          this._appendOptions(selectMunicipio, municipios);
          selectMunicipio.disabled = municipios.length === 0;
        })
        .catch(() => {
          if (this._selectedDepartamentoId !== departamentoId) return;
          selectMunicipio.disabled = true;
        });
    });

    selectMunicipio.addEventListener("change", () => {
      if (!this._map) return;

      if (!selectMunicipio.value) {
        if (this._departamentoBounds) {
          this._map.fitBounds(this._departamentoBounds, { animate: true });
        }
        return;
      }

      const municipio = this._municipios.find((m) => m.id === selectMunicipio.value);
      if (!municipio) return;

      this._map.fitBounds(L.geoJSON(municipio.areaGeografica).getBounds(), {
        animate: true,
      });
    });
  }

  private _appendOptions(
    select: HTMLSelectElement,
    items: (StoredDepartamento | StoredMunicipio)[],
  ): void {
    items.forEach((item) => {
      const option = document.createElement("option");
      option.value = item.id;
      option.textContent = item.nombre;
      select.appendChild(option);
    });
  }

  /** Pone el marcador y lleva el mapa al punto. La promesa se cumple cuando
   *  el mapa termina de llegar (`moveend`), que es cuando se apaga la carga.
   *
   *  El listener se engancha ANTES de `setView`: si el salto es largo Leaflet
   *  no anima y dispara `moveend` en el acto, dentro de la misma llamada. El
   *  tope de tiempo cubre el caso en que el mapa ya estaba en ese punto y
   *  `moveend` nunca llega — sin él, la carga quedaría girando para siempre. */
  private _goToPoint(lat: number, lng: number, zoom: number): Promise<void> {
    const map = this._map;
    if (!map) return Promise.resolve();

    this._quitarMarcador();
    this._searchMarker = L.marker([lat, lng], { icon: SEARCH_MARKER_ICON }).addTo(
      map,
    );

    return new Promise((resolve) => {
      let listo = false;
      const terminar = () => {
        if (listo) return;
        listo = true;
        map.off("moveend", terminar);
        clearTimeout(tope);
        resolve();
      };
      const tope = setTimeout(terminar, TOPE_VIAJE_MS);
      map.on("moveend", terminar);
      map.setView([lat, lng], zoom, { animate: true });
    });
  }
}
